import type { PrV2VersionOption } from '../hooks/use-pr-v2-version-comparison';

import {
  findOptionBySha,
  findPrV2PreviousPushComparison,
} from '../hooks/use-pr-v2-version-comparison';

export type PrV2VersionComparison = {
  baseSha: string;
  headSha: string;
};

/** A one-click comparison: the picker's shortcuts and the command menu's Compare rows. */
export type PrV2ComparisonChoice = {
  id: 'all-changes' | 'previous-push' | 'last-review';
  label: string;
  /** "main -> v3", or null when either version is missing from the options. */
  rangeLabel: string | null;
  comparison: PrV2VersionComparison;
  isActive: boolean;
};

export function getCompactVersionLabel(label: string | undefined, fallback: string): string {
  if (!label) {
    return fallback;
  }

  const compactLabel = label.split(' ')[0]?.trim();
  return compactLabel || fallback;
}

function areComparisonPairsEqual(
  left: PrV2VersionComparison | null,
  right: PrV2VersionComparison | null,
): boolean {
  if (!left || !right) {
    return false;
  }

  return left.baseSha === right.baseSha && left.headSha === right.headSha;
}

function getComparisonRangeLabel(
  options: PrV2VersionOption[],
  comparison: PrV2VersionComparison,
): string | null {
  const baseOption = findOptionBySha(options, comparison.baseSha);
  const headOption = findOptionBySha(options, comparison.headSha);
  if (!baseOption || !headOption) {
    return null;
  }

  return `${getCompactVersionLabel(baseOption.label, 'Base')} -> ${getCompactVersionLabel(
    headOption.label,
    'Latest',
  )}`;
}

/**
 * All changes, Since previous push and Since your last review, in that order, each when it
 * compares an older version with the latest. Since previous push is left out when it is the same
 * pair as Since your last review.
 */
export function getPrV2ComparisonChoices({
  versionOptions,
  selectedBaseSha,
  selectedHeadSha,
  lastReviewedVersionSha,
}: {
  versionOptions: PrV2VersionOption[];
  selectedBaseSha: string | null;
  selectedHeadSha: string | null;
  lastReviewedVersionSha: string | null;
}): PrV2ComparisonChoice[] {
  // An unknown selection reads as the first and last options, as the picker's Base and Head show.
  const selectedBase = findOptionBySha(versionOptions, selectedBaseSha) ?? versionOptions[0];
  const selectedHead = findOptionBySha(versionOptions, selectedHeadSha) ?? versionOptions.at(-1);
  const latestHeadOption =
    versionOptions.find((option) => option.isDefaultHead) ?? versionOptions.at(-1) ?? null;
  const defaultBaseOption =
    versionOptions.find((option) => option.isDefaultBase) ?? versionOptions[0] ?? null;
  const lastReviewedOption = findOptionBySha(versionOptions, lastReviewedVersionSha);

  const allChanges =
    defaultBaseOption && latestHeadOption && defaultBaseOption.index < latestHeadOption.index
      ? { baseSha: defaultBaseOption.id, headSha: latestHeadOption.id }
      : null;
  const previousPush = findPrV2PreviousPushComparison(versionOptions);
  const lastReview =
    lastReviewedOption && latestHeadOption && lastReviewedOption.index < latestHeadOption.index
      ? { baseSha: lastReviewedOption.id, headSha: latestHeadOption.id }
      : null;

  const candidates: Array<[PrV2ComparisonChoice['id'], string, PrV2VersionComparison | null]> = [
    ['all-changes', 'All changes', allChanges],
    [
      'previous-push',
      'Since previous push',
      areComparisonPairsEqual(previousPush, lastReview) ? null : previousPush,
    ],
    ['last-review', 'Since your last review', lastReview],
  ];

  return candidates.flatMap(([id, label, comparison]) =>
    comparison
      ? [
          {
            id,
            label,
            rangeLabel: getComparisonRangeLabel(versionOptions, comparison),
            comparison,
            isActive:
              comparison.baseSha === selectedBase?.id && comparison.headSha === selectedHead?.id,
          },
        ]
      : [],
  );
}
